import * as React from 'react';
import { Button, Form, Col, Row } from 'antd';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { Prompt } from 'react-router';

import { ToastNotifications } from 'src/core/ui/toast';
import { TextView } from 'src/core/ui/text.view';
import { DateView } from 'src/core/ui/date.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { HeaderView } from 'src/app/shared/header.view';
import { CurrencyDefaultRateListController } from '../../currency-default-rates/list/currency-default-rate-list.controller';
import { CurrencyExchangeRateListController } from '../../currency-exchange-rates/list/currency-exchange-rate-list.controller';

interface CurrencyExchangeListProps {
	title: string;
	setBusy(isBusy: boolean);
	save: () => Promise<void>;
	cancel: () => void;
	defaultCurrency: CurrencySummary;
	currencyQuery: () => Promise<CurrencySummary[]>;
	changeDefaultCurrency: (newDefaultCurrency: CurrencySummary) => Promise<boolean>;
	convert(fromCurrencyId: number, toCurrencyId: number, amount: number, mode: number, bookingDate: Date, serviceDate: Date): Promise<number>;
	contextService: any;
}

@observer
export class CurrencyExchangeListView extends React.Component<CurrencyExchangeListProps, any> {
	@resolve
	protected toast: ToastNotifications;

	@observable
	currencies: CurrencySummary[]

	@observable
	type: number;

	@observable
	amount: number;

	@observable
	bookingDate = new Date();

	@observable
	serviceDate = new Date();

	@observable
	from: CurrencySummary;

	@observable
	to: CurrencySummary;

	@observable
	convertedAmount?: number;

	@computed
	get canConvert(): boolean {
		return this.type != null && this.amount != null && (this.bookingDate != null || this.type == 3) && (this.serviceDate != null || this.type == 3) && this.from != null && this.to != null;
	}

	constructor(props: any) {
		super(props);
	}

	componentDidMount() {
		this.props.currencyQuery()
			.then(res => this.currencies = res);
	}

	protected renderTestBox() {
		const currencies = this.currencies != null ? this.currencies.slice() : null;
		return (
			<div className="card card-borderless">
				<h2>Test box</h2>
				<Row gutter={16}>
					<Col xs={24} lg={12}>
						<DropdownView
							label="Type"
							required
							model={this.type}
							onChanged={(item) => this.type = item.value}
							options={
								[
									{ value: 0, label: 'Hedge' },
									{ value: 3, label: 'Default' }
								]
							}
						/>
					</Col>
					<Col xs={24} lg={12}>
						<TextView
							label="Amount"
							mode="edit"
							value={this.amount}
							onChanged={newVal => this.amount = newVal}
						/>
					</Col>
				</Row>
				{
					this.type != 3 &&
					<Row gutter={16}>
						<Col xs={24} lg={12}>
							<DateView
								label="Booking date"
								mode="edit"
								format="small-date"
								value={this.bookingDate}
								onChanged={(newValue) => { this.bookingDate = newValue; }}
								dateUsage="utc"
							/>
						</Col>
						<Col xs={24} lg={12}>
							<DateView
								label="Service date"
								mode="edit"
								format="small-date"
								value={this.serviceDate}
								onChanged={(newValue) => { this.serviceDate = newValue; }}
								dateUsage="utc"
							/>
						</Col>
					</Row>
				}
				<Row gutter={16}>
					<Col xs={24} lg={8}>
						<DropdownView
							label="From"
							model={this.from?this.from.id:null}
							onChanged={(item) => this.from = item}
							keyField="id"
							textField="iso3Name"
							options={currencies}
						/>
					</Col>
					<Col xs={24} lg={8}>
						<DropdownView
							label="To"
							model={this.to ?this.to.id:null}
							onChanged={(item) => this.to = item}
							keyField="id"
							textField="iso3Name"
							options={currencies}
						/>
					</Col>
					<Col xs={24} lg={8}>
						<Form.Item label='&nbsp;' colon={false}>
							<Button type="primary" onClick={this.convert} style={{ width: '100%' }} disabled={!this.canConvert}>Convert</Button>
						</Form.Item>
					</Col>
				</Row>
				{
					this.convertedAmount != null && <Row>
						<Col xs={24}>
							<h2>{this.to.symbol} {this.convertedAmount} {this.to.iso3Name}</h2>
						</Col>
					</Row>
				}
			</div>
		);
	}

	render() {
		if (this.currencies == null) {
			return null;
		}

		return (
			<div className="window window-normal">
				<Prompt
					when={this.props.contextService.isDirty}
					message={location => `If you go to ${location.pathname}, all changes will be lost. Are you sure you want to continue?`}
				/>
				<Row>
					<Col xs={24}>
						<HeaderView
							title={this.props.title}
							mode="edit"
							onModeChanged={null}
							save={this.props.save}
							cancel={this.props.cancel}
							hideBackButton
							contextService={this.props.contextService}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={24}>
						<div className="card">
							<Row gutter={16}>
								<Col xs={24} xl={16}>
									<CurrencyExchangeRateListController
										title="Hedge rate"
										setBusy={this.props.setBusy}
										type={0}
										currencies={this.currencies}
										contextService={this.props.contextService}
										defaultCurrency={this.props.defaultCurrency}
									/>
								</Col>
								<Col xs={24} xl={8}>
									{this.renderTestBox()}
									<CurrencyDefaultRateListController
										setBusy={this.props.setBusy}
										currencies={this.currencies}
										changeDefaultCurrency={this.props.changeDefaultCurrency}
										contextService={this.props.contextService}
										defaultCurrency={this.props.defaultCurrency}
									/>
								</Col>
							</Row>
						</div>
					</Col>
				</Row>
			</div>
		);
	}

	@autobind
	private convert() {
		this.props.convert(this.from.id, this.to.id, this.amount, this.type, this.bookingDate, this.serviceDate)
			.then(res => this.convertedAmount = res)
			.catch(err => this.toast.showToast('Error!', err, 'error'));
	}

	@autobind
	private save() {
		this.props.save()
			.then(() => this.currencies = this.currencies.slice());
	}

	@autobind
	private cancel() {
		this.props.cancel();
	}
}



// WEBPACK FOOTER //
// ./src/app/products/currencies/exchanges/currency-exchanges.view.tsx